'use client';
import React from 'react';
import Image from 'next/image';
import sanitizeHtml from 'sanitize-html';
import { Product } from '../hiv-test/types';
import FeaturesList from './FeaturesList';
import AddToCartButton from './AddToCartButton';

const sanitizeAndRenderHtml = (
  content: string | undefined
): { __html: string } | undefined => {
  if (!content) return undefined;

  const sanitized = sanitizeHtml(content, {
    allowedAttributes: {
      span: ['class'],
    },
    allowedClasses: {
      span: ['*'], // Allow all classes on span elements
    },
    allowedTags: ['b', 'strong', 'i', 'em', 'u', 'span', 'br', 'p'],
  });

  return { __html: sanitized };
};

interface ProductHeroProps {
  title?: string;
  subtitle?: string;
  price?: string;
  priceNote?: string;
  buttonText?: string;
  footerText?: string;
  newsletterLinkText?: string;
  newsletterHref?: string;
  product: Product | null;
  features?: Array<{
    title: string;
    description: string;
  }>;
}

const ProductHero = ({
  buttonText = 'Add to Cart',
  features,
  footerText = "We're vibing in Ontario for now.\nWanna be the first to know when we go long-distance?",
  newsletterHref = '#subscribe',
  newsletterLinkText = 'Slide into our newsletter.',
  price = '$40',
  priceNote = '+ tax and shipping',
  product,
  subtitle = 'Fast, Private and Reliable Results',
  title = 'Trusti HIV 1.0 Rapid Test Kit',
}: ProductHeroProps) => {
  return (
    <section className="heroContainer page-top-spacing-content mt-12 flex w-full flex-col py-[48px] sm:pb-0 md:py-[24px]">
      <div className="content-wrapper flex w-full flex-col md:flex-row md:justify-between">
        <div
          className={`pageHeading mb-10 flex w-full flex-col space-y-6 text-black md:w-[60%]`}
        >
          <h1
            className="mb-4 max-w-[420px] text-[44px]"
            dangerouslySetInnerHTML={sanitizeAndRenderHtml(title)}
          />
          <p
            className="mt-10 mb-6 font-['Spectral'] text-3xl leading-10 font-semibold tracking-[-5%] md:mt-4"
            dangerouslySetInnerHTML={sanitizeAndRenderHtml(subtitle)}
          />
          <Image
            src="/trusti_box.jpg"
            alt="Trusti HIV Rapid Test Kit box"
            width={400}
            height={400}
            className="rounded-lg object-contain"
            priority
          />
          <div className="mb-6 flex items-center gap-3">
            <h2 className="align-middle font-['Work_Sans'] text-[48px] leading-[60px] font-medium tracking-[-5%]">
              {price}
            </h2>
            <span
              className="font-['Work_Sans'] text-xl leading-6 font-medium"
              dangerouslySetInnerHTML={sanitizeAndRenderHtml(priceNote)}
            />
          </div>
          <div className="mt-4 mb-6">
            <AddToCartButton
              product={product}
              buttonText={buttonText}
              className="w-full sm:w-[180px]"
            />
          </div>
          <p className="mt-6 hidden font-['Work_Sans'] text-[15px] leading-8 font-medium uppercase md:block">
            {footerText.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < footerText.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
            <br />
            <a href={newsletterHref} className="underline">
              {newsletterLinkText}
            </a>{' '}
          </p>
        </div>

        <FeaturesList features={features} />
      </div>
    </section>
  );
};

export default ProductHero;
